import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import apiClient, { jobsAPI } from '@/utils/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ShareJobButtons } from '@/components/ShareJobButtons';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Briefcase, MapPin, Eye, Users, Edit, Trash2, PlusCircle,
  CheckCircle, Clock, XCircle, Star, ShieldCheck, Crown, Calendar,
  X, AlertTriangle, CheckSquare, MoreVertical, ArrowRight, RotateCcw, Share2
} from 'lucide-react';

const STATUS_META = {
  active:   { i18nKey: 'active',    color: 'text-emerald-700 bg-emerald-50 border-emerald-200', stripe: 'bg-emerald-500', dot: 'bg-emerald-500' },
  draft:    { i18nKey: 'draft',     color: 'text-amber-700 bg-amber-50 border-amber-200',       stripe: 'bg-amber-400',   dot: 'bg-amber-400' },
  expired:  { i18nKey: 'expired',   color: 'text-gray-600 bg-gray-100 border-gray-200',         stripe: 'bg-gray-300',    dot: 'bg-gray-400' },
  filled:   { i18nKey: 'filled',    color: 'text-blue-700 bg-blue-50 border-blue-200',          stripe: 'bg-blue-500',    dot: 'bg-blue-500' },
  paused:   { i18nKey: 'paused',    color: 'text-gray-700 bg-gray-100 border-gray-200',         stripe: 'bg-gray-300',    dot: 'bg-gray-400' },
  cancelled:{ i18nKey: 'cancelled', color: 'text-gray-700 bg-gray-100 border-gray-200',         stripe: 'bg-gray-300',    dot: 'bg-gray-400' },
  review:   { i18nKey: 'review',    color: 'text-blue-800 bg-blue-50 border-blue-200',           stripe: 'bg-blue-400',    dot: 'bg-blue-500' },
  rejected: { i18nKey: 'rejected',  color: 'text-rose-800 bg-rose-50 border-rose-200',          stripe: 'bg-rose-400',    dot: 'bg-rose-500' }
};
// Moderação: a empresa não pode reactivar sozinha
const statusKey = (job) => (job.status === 'pending_review' || (job.status === 'paused' && job.paused_reason === 'moderation') ? 'review' : job.status);

const daysUntilExpiry = (expiresAt) => {
  if (!expiresAt) return null;
  const diff = new Date(expiresAt).getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

export default function MyJobs() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [confirmModal, setConfirmModal] = useState(null);
  const [shareStats, setShareStats] = useState(null);
  const [actionInFlight, setActionInFlight] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => { loadJobs(); }, []);

  useEffect(() => {
    apiClient.get('/jobs/my/share-stats').then((r) => setShareStats(r.data?.jobs || {})).catch(() => {});
  }, []);

  const loadJobs = async () => {
    try {
      const { data } = await jobsAPI.getMyJobs();
      setJobs(data);
    } catch (error) {
      setMessage(`❌ ${t('myJobs.errorLoad')}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmModal?.job) return;
    setActionInFlight(true);
    try {
      await jobsAPI.delete(confirmModal.job.id);
      setMessage(`✅ ${t('myJobs.deleted')}`);
      setConfirmModal(null);
      loadJobs();
    } catch (error) {
      setMessage(`❌ ${t('myJobs.errorDelete')}`);
    } finally {
      setActionInFlight(false);
    }
  };

  const handleClose = async () => {
    if (!confirmModal?.job) return;
    setActionInFlight(true);
    try {
      await jobsAPI.close(confirmModal.job.id, 'filled');
      setMessage(`✅ ${t('myJobs.markedFilled')}`);
      setConfirmModal(null);
      loadJobs();
    } catch (error) {
      setMessage(`❌ ${t('myJobs.errorClose')}`);
    } finally {
      setActionInFlight(false);
    }
  };

  const handleRepublish = async (jobId) => {
    if (!window.confirm(t('myJobs.confirmRepublish'))) return;
    try {
      const pub = await jobsAPI.publish(jobId);
      setMessage(pub?.data?.moderation ? `✅ ${t('myJobs.status.review')}` : `✅ ${t('myJobs.republished')}`);
      loadJobs();
    } catch (error) {
      const detail = error?.response?.data?.detail || '';
      if (detail.toLowerCase().includes('crédito') || detail.toLowerCase().includes('credito') || detail.toLowerCase().includes('plano')) {
        setMessage(`⚠️ ${t('myJobs.noCreditsRedirect')}`);
        setTimeout(() => navigate('/checkout?plan=simple&republish=' + jobId), 1200);
      } else {
        setMessage('❌ ' + (detail || t('myJobs.errorRepublish')));
      }
    }
  };

  // Counts for filter tabs
  const counts = useMemo(() => {
    const c = { all: jobs.length, active: 0, featured: 0, filled: 0, expired: 0, draft: 0 };
    jobs.forEach(j => {
      if (j.status === 'active') c.active++;
      if (j.status === 'filled') c.filled++;
      if (j.status === 'expired') c.expired++;
      if (j.status === 'draft') c.draft++;
      if (j.is_featured && j.status === 'active') c.featured++;
    });
    return c;
  }, [jobs]);

  const filteredJobs = useMemo(() => {
    if (statusFilter === 'all') return jobs;
    if (statusFilter === 'featured') return jobs.filter(j => j.is_featured && j.status === 'active');
    return jobs.filter(j => j.status === statusFilter);
  }, [jobs, statusFilter]);

  const FILTERS = [
    { key: 'all',      i18nKey: 'all',      count: counts.all },
    { key: 'active',   i18nKey: 'active',   count: counts.active },
    { key: 'featured', i18nKey: 'featured', count: counts.featured },
    { key: 'filled',   i18nKey: 'filled',   count: counts.filled },
    { key: 'expired',  i18nKey: 'expired',  count: counts.expired },
    { key: 'draft',    i18nKey: 'draft',    count: counts.draft }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">{t('myJobs.title')}</h1>
            <p className="text-gray-600 mt-1 text-sm sm:text-base">
              {t('myJobs.jobsPublished', { count: jobs.length })} · {t('myJobs.dailyManagement')}
            </p>
          </div>
          <Button
            onClick={() => navigate('/create-job')}
            className="bg-blue-600 hover:bg-blue-700 shadow-sm"
            data-testid="create-job-btn"
          >
            <PlusCircle className="h-4 w-4 mr-2" />
            {t('myJobs.createNewJob')}
          </Button>
        </div>

        {message && (
          <Alert className={message.includes('✅') ? 'border-green-500 bg-green-50 mb-6' : 'border-red-500 bg-red-50 mb-6'}>
            <AlertDescription>{message}</AlertDescription>
          </Alert>
        )}

        {/* Filter Tabs */}
        {jobs.length > 0 && (
          <Card className="p-2 mb-5 border-gray-200" data-testid="my-jobs-filter-bar">
            <div className="flex items-center gap-1 overflow-x-auto scrollbar-thin">
              {FILTERS.map(f => {
                const active = statusFilter === f.key;
                return (
                  <button
                    key={f.key}
                    onClick={() => setStatusFilter(f.key)}
                    className={`flex-shrink-0 px-3 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
                      active ? 'bg-gray-900 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'
                    }`}
                    data-testid={`my-jobs-filter-${f.key}`}
                  >
                    <span>{t(`myJobs.filter.${f.i18nKey}`)}</span>
                    <span className={`inline-flex items-center justify-center text-xs px-1.5 min-w-[20px] h-5 rounded-full font-semibold ${
                      active ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {f.count}
                    </span>
                  </button>
                );
              })}
            </div>
          </Card>
        )}

        {/* List */}
        {jobs.length === 0 ? (
          <Card className="p-12 text-center">
            <Briefcase className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('myJobs.emptyTitle')}</h3>
            <p className="text-gray-600 mb-6">{t('myJobs.emptyBody')}</p>
            <Button onClick={() => navigate('/create-job')} className="bg-blue-600 hover:bg-blue-700">
              <PlusCircle className="h-4 w-4 mr-2" />{t('myJobs.emptyCta')}
            </Button>
          </Card>
        ) : filteredJobs.length === 0 ? (
          <Card className="p-10 text-center border-dashed">
            <Briefcase className="h-10 w-10 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-700 font-medium">{t('myJobs.noJobsInFilter')}</p>
            <p className="text-gray-500 text-sm mt-1">{t('myJobs.tryAnotherFilter')}</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredJobs.map(job => (
              <JobRow
                shareStats={shareStats}
                key={job.id}
                job={job}
                t={t}
                onViewApps={() => navigate(`/applications?job=${job.id}`)}
                onEdit={() => navigate(`/edit-job/${job.id}`)}
                onMarkFilled={() => setConfirmModal({ type: 'close', job })}
                onDelete={() => setConfirmModal({ type: 'delete', job })}
                onRepublish={() => handleRepublish(job.id)}
                onPublishDraft={() => navigate('/create-job')}
              />
            ))}
          </div>
        )}

        {/* Tips */}
        <div className="mt-8">
          <Card className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Star className="h-4 w-4 text-amber-500" /> {t('myJobs.tipsTitle')}
            </h3>
            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm text-gray-700">
              <li>• {t('myJobs.tip1')}</li>
              <li>• {t('myJobs.tip2')}</li>
              <li>• {t('myJobs.tip3')}</li>
              <li>• {t('myJobs.tip4')}</li>
            </ul>
          </Card>
        </div>
      </div>

      {/* Confirmation modal */}
      {confirmModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="my-jobs-confirm-modal">
          <Card className="max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                {confirmModal.type === 'delete'
                  ? <AlertTriangle className="h-5 w-5 text-red-600" />
                  : <CheckSquare className="h-5 w-5 text-blue-600" />}
                <h2 className="text-lg font-bold">
                  {confirmModal.type === 'delete' ? t('myJobs.confirmDeleteTitle') : t('myJobs.confirmFilledTitle')}
                </h2>
              </div>
              <button onClick={() => setConfirmModal(null)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-gray-700 mb-2">
              {confirmModal.type === 'delete'
                ? t('myJobs.deleteWarning')
                : t('myJobs.closeWarning')}
            </p>
            <p className="text-sm font-semibold text-gray-900 mb-5">&ldquo;{confirmModal.job.title_pt || t('myJobs.untitled')}&rdquo;</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirmModal(null)} disabled={actionInFlight} data-testid="confirm-cancel">
                {t('myJobs.cancel')}
              </Button>
              {confirmModal.type === 'delete' ? (
                <Button variant="destructive" onClick={handleDelete} disabled={actionInFlight} data-testid="confirm-delete">
                  {actionInFlight ? t('myJobs.deleting') : t('myJobs.deletePermanently')}
                </Button>
              ) : (
                <Button onClick={handleClose} disabled={actionInFlight} className="bg-blue-600 hover:bg-blue-700" data-testid="confirm-close">
                  {actionInFlight ? t('myJobs.processing') : t('myJobs.markFilled')}
                </Button>
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

/* -------------------- Job row component -------------------- */
function JobRow({ job, t, shareStats, onViewApps, onEdit, onMarkFilled, onDelete, onRepublish, onPublishDraft }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const meta = STATUS_META[statusKey(job)] || STATUS_META.active;
  const days = daysUntilExpiry(job.expires_at);
  const isDimmed = job.status === 'expired' || job.status === 'filled' || job.status === 'cancelled';
  const appsCount = job.applications_count || 0;

  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  return (
    <Card
      className={`relative overflow-hidden border-gray-200 hover:border-gray-300 hover:shadow-md transition-all ${isDimmed ? 'opacity-80' : ''}`}
      data-testid={`my-job-card-${job.id}`}
    >
      {/* Left status stripe */}
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${meta.stripe}`} />

      <div className="p-5 sm:p-6 pl-6">
        {/* Top row: title + badges + meta */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex-1 min-w-0">
            {/* Status row */}
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-semibold border ${meta.color}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`}></span>
                {t(`myJobs.status.${meta.i18nKey}`)}{job.credit_refund ? ' · crédito devolvido' : ''}
              </span>
              {job.is_featured && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold border border-amber-200 bg-amber-50 text-amber-800" data-testid={`job-badge-featured-${job.id}`}>
                  <Star className="h-3 w-3 fill-amber-500 text-amber-500" />{t('myJobs.filter.featured')}
                </span>
              )}
              {job.badge === 'verified' && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold border border-orange-200 bg-orange-50 text-orange-800">
                  <ShieldCheck className="h-3 w-3" />{t('myJobs.badgeVerified')}
                </span>
              )}
              {job.badge === 'premium' && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold border border-purple-200 bg-purple-50 text-purple-800">
                  <Crown className="h-3 w-3" />{t('myJobs.badgePremium')}
                </span>
              )}
              {job.status === 'active' && days !== null && days <= 7 && days >= 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold border border-amber-300 bg-amber-100 text-amber-900">
                  <AlertTriangle className="h-3 w-3" />
                  {days === 0 ? t('myJobs.expiresToday') : days === 1 ? t('myJobs.expiresTomorrow') : t('myJobs.expiresInDays', { count: days })}
                </span>
              )}
            </div>

            {/* Title */}
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 leading-tight mb-1.5 truncate">
              {job.title_pt || t('myJobs.untitled')}
            </h3>

            {/* Compact meta chips */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-600">
              <span className="inline-flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5 text-gray-400" />{job.location || '—'}
              </span>
              <span className="text-gray-300">·</span>
              <span className="inline-flex items-center gap-1">
                <Briefcase className="h-3.5 w-3.5 text-gray-400" />{job.category || '—'}
              </span>
              {job.salary_range_min && (
                <>
                  <span className="text-gray-300">·</span>
                  <span className="font-medium text-gray-900">
                    {job.salary_range_min.toLocaleString()} – {job.salary_range_max?.toLocaleString()} {job.currency}
                  </span>
                </>
              )}
            </div>

            {/* Stats row */}
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 mt-3 text-sm">
              <div className="flex items-center gap-1.5 text-gray-700">
                <Eye className="h-4 w-4 text-gray-400" />
                <span className="font-semibold">{(job.views_count || 0).toLocaleString()}</span>
                <span className="text-gray-500">{t('myJobs.views')}</span>
              </div>
              {shareStats?.[job.id]?.week > 0 && (
                <div className="flex items-center gap-1.5 text-emerald-700" data-testid={`share-opens-${job.id}`}>
                  <Share2 className="h-4 w-4 text-emerald-500" />
                  <span className="font-semibold">{shareStats[job.id].week}</span>
                  <span className="text-emerald-700/80">{t('myJobs.shareOpens')}</span>
                </div>
              )}
              <div className="flex items-center gap-1.5 text-gray-700">
                <Users className="h-4 w-4 text-gray-400" />
                <span className={`font-semibold ${appsCount > 0 ? 'text-blue-700' : ''}`}>{appsCount.toLocaleString()}</span>
                <span className="text-gray-500">{t('myJobs.applicationsWord', { count: appsCount })}</span>
                {appsCount > 0 && job.status === 'active' && (
                  <span className="ml-1 inline-flex items-center justify-center w-2 h-2 rounded-full bg-blue-500 animate-pulse" title={t('myJobs.needsReview')}></span>
                )}
              </div>
              <div className="flex items-center gap-1.5 text-gray-500 text-xs sm:text-sm">
                <Calendar className="h-3.5 w-3.5" />
                {t('myJobs.createdOn', { date: new Date(job.created_at).toLocaleDateString('pt-PT') })}
              </div>
            </div>
          </div>

          {/* Action column */}
          <div className="flex flex-col sm:flex-row lg:flex-col items-stretch gap-2 lg:min-w-[220px]">
            {/* Primary CTA per status */}
            {job.status === 'draft' && (
              <Button onClick={onPublishDraft} className="bg-emerald-600 hover:bg-emerald-700 shadow-sm" data-testid={`publish-from-draft-${job.id}`}>
                <CheckCircle className="h-4 w-4 mr-2" />{t('myJobs.publishNow')}
              </Button>
            )}
            {job.status === 'active' && (
              <Button
                onClick={onViewApps}
                className="bg-gray-900 hover:bg-gray-800 shadow-sm justify-between"
                data-testid={`view-applications-${job.id}`}
              >
                <span className="inline-flex items-center">
                  <Users className="h-4 w-4 mr-2" />
                  {t('myJobs.applications')} {appsCount > 0 && <span className="ml-2 px-1.5 py-0.5 bg-white/20 rounded text-xs">{appsCount}</span>}
                </span>
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            )}
            {job.status === 'expired' && (
              <Button onClick={onRepublish} className="bg-emerald-600 hover:bg-emerald-700 shadow-sm" data-testid={`republish-job-${job.id}`}>
                <RotateCcw className="h-4 w-4 mr-2" />{t('myJobs.republish')}
              </Button>
            )}
            {job.status === 'filled' && (
              <Button onClick={onViewApps} variant="outline" className="border-blue-200 text-blue-700 hover:bg-blue-50" data-testid={`view-applications-${job.id}`}>
                <Users className="h-4 w-4 mr-2" />{t('myJobs.viewHistory')}
              </Button>
            )}

            {(job.status === 'active' || job.status === 'published') && (
              <ShareJobButtons jobId={job.id} title={job.title_pt} company={job.company_name} compact />
            )}

            <div className="flex gap-2">
              <Button variant="outline" onClick={onEdit} className="flex-1" data-testid={`edit-job-${job.id}`}>
                <Edit className="h-4 w-4 mr-1.5" />{t('myJobs.edit')}
              </Button>

              {/* Secondary actions menu */}
              <div className="relative" ref={menuRef}>
                <Button
                  variant="outline"
                  onClick={() => setMenuOpen(o => !o)}
                  className="px-2.5"
                  aria-label={t('myJobs.moreActions')}
                  data-testid={`job-more-menu-${job.id}`}
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
                {menuOpen && (
                  <div className="absolute right-0 mt-1 w-52 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1">
                    {job.status === 'active' && (
                      <button
                        onClick={() => { setMenuOpen(false); onMarkFilled(); }}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 flex items-center gap-2"
                        data-testid={`mark-filled-${job.id}`}
                      >
                        <CheckSquare className="h-4 w-4 text-blue-600" />{t('myJobs.markFilledShort')}
                      </button>
                    )}
                    <button
                      onClick={() => { setMenuOpen(false); onDelete(); }}
                      className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                      data-testid={`delete-job-${job.id}`}
                    >
                      <Trash2 className="h-4 w-4" />{t('myJobs.deleteJob')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}
